/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback } from "react";

import { emitEvent } from "@components/utils/analytics";

import { useCookie } from "./cookie.hooks";
import { useIsBrowser, useIsMac, useIsMobile } from "./userAgent.hooks";

/**
 * Programmatically triggers a file download via a temporary anchor element and emits an analytics event.
 * @param epicSid - Optional Epic session ID appended as `trackingId` query param.
 */
export const downloadHandler = (downloadUrl: string, component: string, eventLabel: string, epicSid?: string): void => {
	const link = document.createElement("a");
	const url = new URL(downloadUrl);
	if (epicSid) url.searchParams.append("trackingId", epicSid);
	const urlString = url.toString();
	link.href = urlString;
	link.setAttribute("download", "");
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	emitEvent({
		eventCategory: "trigger",
		interactionType: "click",
		eventAction: "navigation",
		eventLabel,
		eventValue: {
			component,
			urlString,
		},
	});
};

/** Removes the `start` query parameter from the current URL via `history.pushState`, preserving all other params. */
export const removeStartParam = (searchParams: string): void => {
	const newSearchParams = new URLSearchParams(searchParams);
	newSearchParams.delete("start");
	const newParams = newSearchParams.toString();
	const pathname = window.location.pathname;
	const newUrl = newParams.length > 0 ? `${pathname}?${newParams}` : pathname;
	history.pushState(null, "", newUrl);
};

/**
 * Handles downloading the launcher installer for the correct platform; doesn't download on mobile
 */
export const useDownloadLauncher = (windowsUrl: string, macUrl: string): (() => void) => {
	const isMac = useIsMac();
	const isMobile = useIsMobile();
	const epicSid = useCookie("_epicSID");
	const isBrowser = useIsBrowser();
	const downloadUrl = isMac ? macUrl : windowsUrl;

	const initiateDownload = useCallback(() => {
		if (isMobile) return;
		if (!downloadUrl) return;
		if (!isBrowser) return;
		downloadHandler(downloadUrl, "DownloadLauncher", "trigger-launcher-download", epicSid);
		return;
	}, [epicSid, isMobile, isBrowser, downloadUrl]);

	return initiateDownload;
};

/**
 * Returns a callback that triggers a download for any URL; no-ops on mobile or SSR.
 * Intended for auto-starting downloads on page load.
 */
export const useAutoStartDownload = (downloadUrl: string, component?: string): (() => void) => {
	const isMobile = useIsMobile();
	const epicSid = useCookie("_epicSID");
	const isBrowser = useIsBrowser();

	const initiateDownload = useCallback(() => {
		if (isMobile) return;
		if (!downloadUrl) return;
		if (!isBrowser) return;
		downloadHandler(downloadUrl, component || "", "cosmos-download", epicSid);
		return;
	}, [epicSid, isMobile, isBrowser, downloadUrl, component]);

	return initiateDownload;
};
